import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import Card from './Card'
import Button from './Button'
import Select from './Select'
import { GitCompare, ArrowRight, TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { getMedicalHistoryVersions } from '../services/medicalHistoryVersions'
import { formatDate } from '../utils/helpers'

const HistoryComparisonView = ({ clientId, currentHistory, organizationId }) => {
  const [versions, setVersions] = useState([])
  const [selectedVersion1, setSelectedVersion1] = useState(null)
  const [selectedVersion2, setSelectedVersion2] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (clientId && currentHistory?.id && organizationId) {
      fetchVersions()
    }
  }, [clientId, currentHistory?.id, organizationId])

  const fetchVersions = async () => {
    if (!currentHistory?.id || !organizationId) return
    try {
      setLoading(true)
      const data = await getMedicalHistoryVersions(currentHistory.id, organizationId)
      // Add current version as latest
      setVersions([
        {
          id: 'current',
          version_number: (data[0]?.version_number || 0) + 1,
          history_data: {
            current_medications: currentHistory.current_medications,
            allergies: currentHistory.allergies,
            past_surgeries: currentHistory.past_surgeries,
            family_history: currentHistory.family_history,
            chronic_conditions: currentHistory.chronic_conditions,
            diet: currentHistory.diet,
            exercise: currentHistory.exercise,
            sleep: currentHistory.sleep,
            stress_level: currentHistory.stress_level,
            alcohol: currentHistory.alcohol,
            smoking: currentHistory.smoking,
            primary_goals: currentHistory.primary_goals,
            expectations: currentHistory.expectations,
            previous_treatments: currentHistory.previous_treatments,
          },
          created_at: new Date().toISOString(),
          is_current: true,
        },
        ...data,
      ])
    } catch (error) {
      console.error('Error fetching versions:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatValue = (value) => {
    if (!value) return null
    
    // Handle habit arrays (diet, exercise, sleep)
    if (Array.isArray(value) && value.length > 0) {
      // Check if it's a habit array (has objects with 'text' property)
      if (value[0] && typeof value[0] === 'object' && 'text' in value[0]) {
        const habits = value.map((habit) => habit.text).filter(Boolean)
        if (habits.length === 0) return null
        return habits.join('\n• ')
      }
    }
    
    // Handle string values
    return value
  }

  const getChangeIndicator = (oldValue, newValue) => {
    // Normalize values for comparison
    const oldFormatted = formatValue(oldValue)
    const newFormatted = formatValue(newValue)
    
    if (!oldFormatted && newFormatted) return { icon: TrendingUp, color: 'text-green-600', label: 'Added' }
    if (oldFormatted && !newFormatted) return { icon: TrendingDown, color: 'text-red-600', label: 'Removed' }
    if (oldFormatted !== newFormatted) return { icon: GitCompare, color: 'text-blue-600', label: 'Changed' }
    return { icon: Minus, color: 'text-gray-400', label: 'Unchanged' }
  }

  const version1Data = selectedVersion1?.history_data || {}
  const version2Data = selectedVersion2?.history_data || {}

  const fields = [
    { key: 'current_medications', label: 'Current Medications' },
    { key: 'allergies', label: 'Allergies' },
    { key: 'past_surgeries', label: 'Past Surgeries' },
    { key: 'family_history', label: 'Family History' },
    { key: 'chronic_conditions', label: 'Chronic Conditions' },
    { key: 'diet', label: 'Diet' },
    { key: 'exercise', label: 'Exercise' },
    { key: 'sleep', label: 'Sleep' },
    { key: 'stress_level', label: 'Stress Level' },
    { key: 'alcohol', label: 'Alcohol' },
    { key: 'smoking', label: 'Smoking' },
    { key: 'primary_goals', label: 'Primary Goals' },
    { key: 'expectations', label: 'Expectations' },
    { key: 'previous_treatments', label: 'Previous Treatments' },
  ]

  const versionOptions = versions.map((v) => ({
    value: v.id,
    label: `Version ${v.version_number}${v.is_current ? ' (Current)' : ''} - ${formatDate(v.created_at)}`,
  }))

  if (loading) {
    return <div className="text-sm text-gray-500">Loading history versions...</div>
  }

  if (versions.length === 0) {
    return (
      <Card variant="soft" className="p-6 text-center">
        <GitCompare size={32} className="mx-auto mb-2 text-gray-400" />
        <p className="text-sm text-gray-500">No version history available</p>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <GitCompare size={20} />
        <h3 className="text-lg font-semibold text-gray-900">History Comparison</h3>
      </div>

      {/* Version Selectors */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Version 1 (Before)</label>
          <Select
            options={versionOptions}
            value={selectedVersion1 || ''}
            onChange={(e) => {
              const version = versions.find((v) => v.id === e.target.value)
              setSelectedVersion1(version)
            }}
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Version 2 (After)</label>
          <Select
            options={versionOptions}
            value={selectedVersion2 || ''}
            onChange={(e) => {
              const version = versions.find((v) => v.id === e.target.value)
              setSelectedVersion2(version)
            }}
          />
        </div>
      </div>

      {/* Comparison View */}
      {selectedVersion1 && selectedVersion2 && (
        <div className="space-y-3">
          {fields.map((field) => {
            const v1Value = version1Data[field.key] || ''
            const v2Value = version2Data[field.key] || ''
            const change = getChangeIndicator(v1Value, v2Value)
            const ChangeIcon = change.icon

            return (
              <Card key={field.key} variant="soft" className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <ChangeIcon size={16} className={change.color} />
                  <h4 className="font-semibold text-gray-900">{field.label}</h4>
                  <span className={`text-xs font-semibold ${change.color}`}>{change.label}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                    <p className="text-xs font-semibold text-red-700 mb-1">Version {selectedVersion1.version_number}</p>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {formatValue(v1Value) || <span className="text-gray-400 italic">No data</span>}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-green-50 border border-green-200">
                    <p className="text-xs font-semibold text-green-700 mb-1">Version {selectedVersion2.version_number}</p>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {formatValue(v2Value) || <span className="text-gray-400 italic">No data</span>}
                    </p>
                  </div>
                </div>
              </Card>
            )
          })}
        </div>
      )}

      {(!selectedVersion1 || !selectedVersion2) && (
        <Card variant="soft" className="p-6 text-center">
          <p className="text-sm text-gray-500">Select two versions to compare</p>
        </Card>
      )}
    </div>
  )
}

export default HistoryComparisonView

